import { MsalProvider } from '@azure/msal-react';
import { Layout } from '@organisms/Layout';
import { msalInstance, initGA, logPageView } from '@utils';
import { isValidWasteCategoryAndRoute } from '../utils/isValidWasteCategoryAndRoute';

import { useEffect } from 'react';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  AdditionalDetails,
  BeforeYouStart,
  Confirmation,
  EmailConfirmationReport,
  TestLandingPage,
  UploadPhoto,
  WasteDetails,
  WasteLocation,
  YourDetails,
} from './components/pages';
import { RoutesList, steps } from './routes/Routes';
import { useWasteStore } from '../store/store';
import { basename } from './routes/basename';
import { InternalServerErrorTemplate, NotFoundErrorTemplate } from '@templates';

function App() {
  useEffect(() => {
    initGA();
    logPageView();

    // Log page views on route change
    const logPageViewOnRouteChange = () => logPageView();
    window.addEventListener('popstate', logPageViewOnRouteChange);

    return () =>
      window.removeEventListener('popstate', logPageViewOnRouteChange);
  }, []);
  // It's important that the Router component is above the pages component because you'll need to use the useHistory hook before rendering MsalProvider
  return (
    <Router basename={basename}>
      <RouteChangeTracker />
      <Pages />
    </Router>
  );
}

function Pages() {
  const location = useLocation();
  const navigate = useNavigate();
  const { wasteCategory } = useWasteStore();

  // Monitors the waste category and pathname to ensure correct category is selected from landing page
  useEffect(() => {
    // @ts-ignore
    if (!isValidWasteCategoryAndRoute(wasteCategory, location.pathname)) {
      navigate(RoutesList.NOT_FOUND);
    }
  }, [wasteCategory, location.pathname, navigate]);

  return (
    <MsalProvider instance={msalInstance}>
      <Layout steps={steps}>
        <Routes>
          <Route
            path={RoutesList.TEST_LANDING_PAGE}
            element={<TestLandingPage />}
          />
          <Route path={RoutesList.WASTE_HOME} element={<BeforeYouStart />} />
          <Route path={RoutesList.UPLOAD_PHOTO} element={<UploadPhoto />} />
          <Route path={RoutesList.WASTE_DETAILS} element={<WasteDetails />} />
          <Route
            path={RoutesList.ADDITIONAL_DETAILS}
            element={<AdditionalDetails />}
          />
          <Route path={RoutesList.WASTE_LOCATION} element={<WasteLocation />} />

          <Route path={RoutesList.YOUR_DETAILS} element={<YourDetails />} />
          <Route
            path={RoutesList.EMAIL_CONFIRMATION_REPORT}
            element={<EmailConfirmationReport />}
          />
          {/*  the line below is an interim solution to prevent 404 on redirect, it will be replaced with NavigationClient from '@azure/msal-browser */}
          <Route path={RoutesList.CALLBACKS} element={null} />
          <Route path={RoutesList.CONFIRMATION} element={<Confirmation />} />
          <Route
            path={RoutesList.INTERNAL_SERVER_ERROR}
            element={<InternalServerErrorTemplate />}
          />
          <Route
            path={RoutesList.NOT_FOUND}
            element={<NotFoundErrorTemplate />}
          />
        </Routes>
      </Layout>
    </MsalProvider>
  );
}

// Component to track route changes and log page views
export const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    logPageView();
  }, [location]);

  return null;
};
export default App;
